import React, { useState, useEffect } from 'react';
import './AdminEmployee.css';
import './EmployeeModal.css';
import Navbar from '../Navbar/Navbar';
import TopBar from '../Navbar/Mainbar.jsx';
import Swal from 'sweetalert2';
import EmployeeFormModal, {
  getApiBase, getInitials, escapeHtml, showAlert, CloseIcon,
} from './EmployeeFormModal';

const ROWS_PER_PAGE = 10;

const getRoleColor = (role) => ({
  Employee: '#3b82f6', Manager: '#16a34a', HR: '#ea580c',
  CEO: '#7c3aed', Superadmin: '#dc2626', SUPERADMIN: '#dc2626', Consultant: '#0891b2'
}[role] || '#6b7280');

// Show only the state. Old rows stored the country in `state`, so hide values like "India".
const formatLocation = (emp) => {
  const state = emp.state?.trim();
  return state && state !== emp.location?.trim() ? state : '';
};

const EmployeeManagement = () => {
  // formEmp: null = form closed, {} = Add, employee row = Edit
  const [formEmp,     setFormEmp]     = useState(null);
  const [viewEmp,     setViewEmp]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [employees,   setEmployees]   = useState([]);
  const [searchTerm,  setSearchTerm]  = useState(''); // eslint-disable-line no-unused-vars
  const [deptFilter,  setDeptFilter]  = useState('');
  const [roleFilter,  setRoleFilter]  = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortDir,   setSortDir]   = useState('asc');


  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color: '#ffffff60', marginLeft: '4px', fontSize: '11px' }}>⇅</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const apiFetch = (path, opts = {}) =>
    fetch(`${getApiBase()}${path}`, { credentials: 'include', headers: { 'Accept': 'application/json' }, ...opts });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [leaveRes, empRes] = await Promise.all([
        apiFetch(`/api/employees/batch-leave-data?_=${Date.now()}`),
        apiFetch(`/api/employee-list?_=${Date.now()}`)
      ]);
      const leaveData = leaveRes.ok ? await leaveRes.json() : {};
      if (!empRes.ok) throw new Error('Failed to fetch employees');
      const raw = await empRes.json();
      const active = raw.filter(e => !e.is_deleted && e.status !== 'deleted' && e.status !== 'inactive' && e.is_active !== false);
      const enriched = active.map(emp => {
        const ld = leaveData[emp.id] || { on_leave_today: false, work_status: 'active', work_status_reason: 'Present' };
        return { ...emp, status: ld.work_status || emp.status || 'active', on_leave_today: ld.on_leave_today || false };
      });
      setEmployees(enriched);
      setCurrentPage(1);
    } catch (e) {
      showAlert({ type: 'error', title: 'Failed to load', messageHtml: 'Could not load employees. Please try again.', confirmText: 'Retry' })
        .then(r => { if (r.isConfirmed) loadAll(); });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // View modal: close with Escape + lock page scroll while open
  // (the Add / Edit form handles this itself)
  useEffect(() => {
    if (!viewEmp) return;
    const onKey = (e) => {
      if (e.key !== 'Escape' || Swal.isVisible()) return;
      setViewEmp(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [viewEmp]);

  // ── filter + sort ─────────────────────────────────────────
  const filtered = employees
    .filter(emp => {
      const s = searchTerm.toLowerCase();
      const okSearch = emp.name?.toLowerCase().includes(s) || emp.email?.toLowerCase().includes(s);
      const okDept   = !deptFilter || emp.department === deptFilter;
      const okRole   = !roleFilter || emp.role === roleFilter;
      return okSearch && okDept && okRole;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      if (sortField === 'sno') {
        const diff = employees.indexOf(a) - employees.indexOf(b);
        return sortDir === 'asc' ? diff : -diff;
      }
      const pick = {
        name:       e => e.name || '',
        role:       e => e.role || '',
        employeeid: e => e.employeeid || '',
        department: e => e.department || '',
        location:   e => formatLocation(e),
        manager:    e => e.manager_name || '',
        joinDate:   e => e.joinDate || e.created_at || '',
        status:     e => e.on_leave_today ? 'On Leave' : 'Active',
      }[sortField];
      if (!pick) return 0;
      const cmp = pick(a).localeCompare(pick(b));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // ── pagination ────────────────────────────────────────────
  const totalRecords  = filtered.length;
  const totalPages    = Math.max(1, Math.ceil(totalRecords / ROWS_PER_PAGE));
  const safePage      = Math.min(currentPage, totalPages);
  const startIndex    = (safePage - 1) * ROWS_PER_PAGE;
  const endIndex      = Math.min(startIndex + ROWS_PER_PAGE, totalRecords);
  const paginated     = filtered.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, deptFilter, roleFilter]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const uniqueDepts = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const uniqueRoles = [...new Set(employees.map(e => e.role).filter(Boolean))];

  const openAdd  = () => setFormEmp({});
  const openEdit = (emp) => { setViewEmp(null); setFormEmp(emp); };

  const handleDelete = async (id, name) => {
    const safeName = escapeHtml(name);
    const confirm = await showAlert({
      type: 'warning',
      title: 'Deactivate employee?',
      messageHtml: `This will prevent <strong>${safeName}</strong> from logging in but keeps their records.`,
      confirmText: 'Yes, deactivate',
      confirmColor: '#dc2626',
      showCancel: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await apiFetch(`/api/delete-employee/${id}?_=${Date.now()}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      const resultData = await res.json();
      if (!res.ok) throw new Error(resultData.error || 'Failed to deactivate');

      setViewEmp(null);
      await showAlert({ title: 'Deactivated', messageHtml: `<strong>${safeName}</strong> has been deactivated.` });
      await loadAll();
    } catch (err) {
      showAlert({ type: 'error', title: 'Could not deactivate', messageHtml: escapeHtml(err.message || 'Failed to deactivate employee.'), confirmText: 'Close' });
    }
  };

  const handleExport = () => {
    try {
      const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const headers = ['S.No', 'Name', 'Employee Code', 'Email', 'Role', 'Department', 'Address', 'State', 'Country', 'Phone', 'Alternate Phone', 'Manager', 'Joining Date', 'Status'];
      const csv = [headers.join(','), ...filtered.map((e, index) => [
        index + 1, q(e.name), q(e.employeeid), q(e.email), q(e.role), q(e.department),
        q(e.address), q(e.state !== e.location ? e.state : ''), q(e.location),
        q(e.ContactNumber1 || e.contact_number_1), q(e.ContactNumber2 || e.contact_number_2),
        q(e.manager_name), q(e.joinDate), q(e.on_leave_today ? 'On Leave' : (e.status || 'Active')),
      ].join(','))].join('\n');

      const [year, month, day] = new Date().toISOString().split('T')[0].split('-');
      const filename = `employees_${month}-${day}-${year}.csv`;
      const a = Object.assign(document.createElement('a'), {
        href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
        download: filename
      });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);

      showAlert({ title: 'Export complete', messageHtml: `Employee data was exported as <strong>${filename}</strong>` });
    } catch {
      showAlert({ type: 'error', title: 'Export failed', messageHtml: 'Something went wrong while exporting. Please try again.', confirmText: 'Close' });
    }
  };

  const StatusBadge = ({ emp }) => emp.on_leave_today ? (
    <span className="Admin-employee-status-badge on-leave">On Leave</span>
  ) : (
    <span className="Admin-employee-status-badge">Active</span>
  );

  const paginationStyles = {
    wrapper: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderTop: '1px solid #e5e7eb', marginTop: '0', flexWrap: 'wrap', gap: '12px' },
    info: { fontSize: '13px', color: '#6b7280' },
    controls: { display: 'flex', alignItems: 'center', gap: '4px' },
    btn: (active, disabled) => ({
      minWidth: '32px', height: '32px', padding: '0 8px',
      border: active ? '1.5px solid #3b82f6' : '1px solid #e5e7eb', borderRadius: '6px',
      background: active ? '#3b82f6' : disabled ? '#f9fafb' : '#fff',
      color: active ? '#fff' : disabled ? '#d1d5db' : '#374151',
      fontSize: '13px', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: active ? '500' : '400',
      transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }),
    ellipsis: { minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: '#9ca3af' },
  };

  return (
    <div className="Admin-employee-navbar" style={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
      <div className="Admin-emp-nav"><Navbar /></div>

      <div className="Admin-employee-container">
        <TopBar />

        <div className="emp-body">
          <div className="Admin-employee-header">
            <div className="Admin-employee-title-section">
              <h1>Employee Management</h1>
              <p className="Admin-employee-subtitle">Manage all employee accounts and permissions</p>
            </div>
          </div>

          <button className="Admin-employee-add-btn" onClick={openAdd}>
            <span className="Admin-employee-add-icon">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            Add Employee
          </button>

          <div className="Admin-employee-filters-card">
            <div className="Admin-employee-filter-group">
              <label className="Admin-employee-filter-label">Department</label>
              <select className="Admin-employee-filter-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                <option value="">All Departments</option>
                {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="Admin-employee-filter-group">
              <label className="Admin-employee-filter-label">Role</label>
              <select className="Admin-employee-filter-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button className="Admin-employee-export-btn" onClick={handleExport} disabled={filtered.length === 0}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export
            </button>
          </div>

          <div className="Admin-employee-table-section">
            <h2 className="Admin-employee-table-title">Employee Details ({totalRecords})</h2>
            <div className="Admin-employee-table-wrapper">
              {loading ? (
                <div className="emp-table-state">Loading employees…</div>
              ) : totalRecords === 0 ? (
                <div className="emp-table-state">No employees found</div>
              ) : (
                <>
                  <table className="Admin-employee-table">
                    <thead>
                      <tr className="Admin-employee-table-header">
                        {[
                          ['sno', 'S.No'], ['name', 'Employee'], ['role', 'Role'], ['employeeid', 'Employee Code'],
                          ['department', 'Department'], ['location', 'State'], ['manager', 'Manager'],
                          ['joinDate', 'Joining Date'], ['status', 'Status'],
                        ].map(([field, label]) => (
                          <th key={field} onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            {label} <SortIcon field={field} />
                          </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map(emp => (
                        <tr key={emp.id} className="Admin-employee-table-row">
                          <td className="Admin-employee-cell" style={{ fontWeight: '500' }}>{employees.indexOf(emp) + 1}</td>
                          <td className="Admin-employee-cell">
                            <div className="Admin-employee-name-cell">
                              <div className="Admin-employee-name">{emp.name}</div>
                              <div className="Admin-employee-email">{emp.email}</div>
                            </div>
                          </td>
                          <td className="Admin-employee-cell">
                            <span className="Admin-employee-role-badge" style={{ color: getRoleColor(emp.role) }}>{emp.role}</span>
                          </td>
                          <td className="Admin-employee-cell" style={{ fontWeight: '500', color: '#4b5563' }}>
                            {emp.employeeid || emp.employee_id || '—'}
                          </td>
                          <td className="Admin-employee-cell">{emp.department}</td>
                          <td className="Admin-employee-cell">{formatLocation(emp) || 'Not specified'}</td>
                          <td className="Admin-employee-cell">{emp.manager_name || 'No manager'}</td>
                          <td className="Admin-employee-cell">{emp.joinDate || emp.created_at?.split('T')[0] || 'N/A'}</td>
                          <td className="Admin-employee-cell"><StatusBadge emp={emp} /></td>
                          <td className="Admin-employee-cell">
                            <div className="Admin-employee-actions">
                              <button
                                className="Admin-employee-action-btn Admin-employee-view-btn"
                                onClick={() => setViewEmp(emp)}
                                title="View employee"
                                aria-label={`View ${emp.name}`}
                              >
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <circle cx="12" cy="12" r="3" stroke="#16a34a" strokeWidth="2"/>
                                </svg>
                              </button>
                              <button
                                className="Admin-employee-action-btn Admin-employee-edit-btn"
                                onClick={() => openEdit(emp)}
                                title="Edit employee"
                                aria-label={`Edit ${emp.name}`}
                              >
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button
                                className="Admin-employee-action-btn Admin-employee-delete-btn"
                                onClick={() => handleDelete(emp.id, emp.name)}
                                title="Deactivate employee"
                                aria-label={`Deactivate ${emp.name}`}
                              >
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                                  <polyline points="3 6 5 6 21 6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {totalPages > 1 && (
                    <div style={paginationStyles.wrapper}>
                      <span style={paginationStyles.info}>Showing {startIndex + 1}–{endIndex} of {totalRecords} employees</span>
                      <div style={paginationStyles.controls}>
                        <button style={paginationStyles.btn(false, safePage === 1)} onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous page">‹</button>
                        {getPageNumbers().map((page, idx) =>
                          page === '...'
                            ? <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                            : <button key={page} style={paginationStyles.btn(page === safePage, false)} onClick={() => goToPage(page)}>{page}</button>
                        )}
                        <button style={paginationStyles.btn(false, safePage === totalPages)} onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next page">›</button>
                      </div>
                    </div>
                  )}

                  {totalPages === 1 && totalRecords > 0 && (
                    <div style={{ ...paginationStyles.wrapper, justifyContent: 'flex-end' }}>
                      <span style={paginationStyles.info}>{totalRecords} employee{totalRecords !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>


        {/* ═══════════════ Add / Edit modal (shared with Admin Dashboard) ═══════════════ */}
        <EmployeeFormModal
          open={formEmp !== null}
          employee={formEmp && formEmp.id ? formEmp : null}
          employees={employees}
          onClose={() => setFormEmp(null)}
          onSaved={() => loadAll()}
        />

        {/* ═══════════════ View modal ═══════════════ */}
        {viewEmp && (() => {
          const e = viewEmp;
          const state = e.state && e.state !== e.location ? e.state : '';
          const items = [
            ['Employee code', e.employeeid || e.employee_id],
            ['Joining date', e.joinDate || e.join_date || e.created_at?.split('T')[0]],
            ['Role', e.role],
            ['Department', e.department],
            ['Reporting manager', e.manager_name],
            ['Status', e.on_leave_today ? 'On leave today' : 'Active'],
            ['Phone', e.ContactNumber1 || e.contact_number_1],
            ['Alternate phone', e.ContactNumber2 || e.contact_number_2],
            ['State', state],
            ['Country', e.location],
          ];
          return (
            <div className="emp-modal-overlay" onMouseDown={ev => { if (ev.target === ev.currentTarget) setViewEmp(null); }}>
              <div className="emp-modal emp-modal--view" role="dialog" aria-modal="true" aria-labelledby="emp-view-title">
                <header className="emp-modal-header">
                  <div className="emp-avatar" aria-hidden="true">{getInitials(e.name)}</div>
                  <div className="emp-modal-heading">
                    <p className="emp-modal-kicker">{e.email}</p>
                    <h2 id="emp-view-title" className="emp-modal-title">{e.name}</h2>
                  </div>
                  <button type="button" className="emp-modal-close" onClick={() => setViewEmp(null)} aria-label="Close">
                    <CloseIcon />
                  </button>
                </header>

                <div className="emp-modal-body">
                  <div className="emp-view-grid">
                    {items.map(([label, value]) => (
                      <div key={label} className="emp-view-item">
                        <div className="emp-view-label">{label}</div>
                        <div className={`emp-view-value${value ? '' : ' is-empty'}`}>{value || 'Not provided'}</div>
                      </div>
                    ))}
                    <div className="emp-view-item emp-view-item--full">
                      <div className="emp-view-label">Address</div>
                      <div className={`emp-view-value${e.address ? '' : ' is-empty'}`}>{e.address || 'Not provided'}</div>
                    </div>
                  </div>
                </div>

                <footer className="emp-modal-footer">
                  <button type="button" className="emp-btn emp-btn--ghost" style={{ color: '#dc2626' }}
                    onClick={() => handleDelete(e.id, e.name)}>
                    Deactivate
                  </button>
                  <div className="emp-footer-actions">
                    <button type="button" className="emp-btn emp-btn--ghost" onClick={() => setViewEmp(null)}>Close</button>
                    <button type="button" className="emp-btn emp-btn--primary" onClick={() => openEdit(e)}>Edit employee</button>
                  </div>
                </footer>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default EmployeeManagement;